import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from "../context/TranslationContext";
import '../styles/InputGallery.css';
import Button from './Button';
import Image from "./Image";
import { ReactComponent as IconPhotoSelect } from '../resources/assets/iconPhotoSelect.svg';
import config from '../config'; 

import { optimizeImage } from "../utils/Images";

const InputGallery = ({ mode, module, name, list = [], max, onListChange, timestamp }) => {
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const listGalleryRef = useRef(null);
  const resizeIntervalRef = useRef(null);
  const resizeIntervalCounterRef = useRef(null);
  const [images, setImages] = useState(list);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [draggedOverIndex, setDraggedOverIndex] = useState(null);
  const dragImageRef = useRef(null);

  const isEmpty = images.length === 0;
  const isDelete = images.length>1;

  const waitForGalleryRef = (img) => {
    resizeIntervalRef.current = setInterval(() => {
      resizeIntervalCounterRef.current++;
      if(resizeIntervalCounterRef.current>5) {
        clearInterval(resizeIntervalRef.current); 
      }
      if (listGalleryRef.current) {
        clearInterval(resizeIntervalRef.current); 
        handleResize(img); 
      }
    }, 100);
  };
  
  const handleResize = (img) => {  
    if (!listGalleryRef.current) {
      resizeIntervalCounterRef.current = 0;
      waitForGalleryRef(img); 
      return;
    }
  
    let galleryImageSize = parseInt(
      getComputedStyle(document.documentElement)
        .getPropertyValue("--gallery-image-size")
        .trim()
    );
  
    let padding = parseInt(
      getComputedStyle(document.documentElement)
        .getPropertyValue("--padding")
        .trim()
    );
  
    let screenWidth = window.innerWidth - padding * 2;
    let number = Math.floor(screenWidth / (galleryImageSize + padding));
    if(number>img.length+1) {
      number = img.length+1;
    }
    let width = number * (galleryImageSize + padding);  
    listGalleryRef.current.style.width = width + "px";
  };

  useEffect(() => {
    window.addEventListener("resize", handleResize);
    handleResize(images);
    
    return () => {
      clearInterval(resizeIntervalRef.current); 
      window.removeEventListener("resize", handleResize);
      dragImageClean();
    };
  }, []);

  const addImage = () => {
    fileInputRef.current.click();
  };

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files);
    const remainingSlots = max - images.length;
  
    if (remainingSlots > 0) {
      const filesToProcess = selectedFiles.slice(0, remainingSlots);
      
      const placeholderImages = new Array(filesToProcess.length).fill(null);
      const placeholderIndexes = placeholderImages.map((_, i) => images.length + i);
      const updatedImages = [...images, ...placeholderImages];
      setImages(updatedImages);
      handleResize(updatedImages);
  
      optimizeImage(filesToProcess, config.gallery.normal.maxWidth, config.gallery.normal.maxHeight, config.gallery.normal.maxSize)
      .then((optimized) => {
        const finalImages = [...updatedImages];
        optimized.forEach((img, i) => {
          finalImages[placeholderIndexes[i]] = img;
        });

        setImages(finalImages);
        if (onListChange) onListChange(finalImages);
        handleResize(finalImages);
      })
      .catch(error => {
        console.log("error: " + error);
      });
    }
  };

  const handleDelete = (index) => {
    const updatedImages = images.filter((_, i) => i !== index);
    setImages(updatedImages);
    if (onListChange) onListChange(updatedImages);
    handleResize(updatedImages);
  };

  const handleDragStart = (event, index) => {
    dragImageClean();

    setDraggedIndex(index);
    const isTouch = event.type.startsWith("touch");

    const clientX = isTouch ? event.touches[0].clientX : event.clientX;
    const clientY = isTouch ? event.touches[0].clientY : event.clientY;

    const imgClone = event.target.children[0].cloneNode(true);
    const deleteButton = imgClone.querySelector('.image-list-image-delete');
    if (deleteButton) {
        deleteButton.remove();
    }
    imgClone.style.pointerEvents = "none";

    const rootStyles = getComputedStyle(document.documentElement);
    var galleryImageSize = parseInt(rootStyles.getPropertyValue('--gallery-image-size').trim(),10);

    imgClone.classList.add("image-list-gallery-dragging-preview");
    imgClone.style.left = `${clientX - galleryImageSize / 2}px`;
    imgClone.style.top = `${clientY - galleryImageSize / 2}px`;

    document.body.appendChild(imgClone);
    dragImageRef.current = imgClone;

    document.addEventListener(isTouch ? "touchmove" : "mousemove", handleDragMove);
    document.addEventListener(isTouch ? "touchend" : "mouseup", handleDragEnd);
  };

  const handleDragMove = (event) => {
    if (!dragImageRef.current) return;

    const isTouch = event.type.startsWith("touch");

    const clientX = isTouch ? event.touches[0].clientX : event.clientX;
    const clientY = isTouch ? event.touches[0].clientY : event.clientY;

    dragImageRef.current.style.left = `${clientX - dragImageRef.current.offsetWidth / 2}px`;
    dragImageRef.current.style.top = `${clientY - dragImageRef.current.offsetHeight / 2}px`;

    const element = document.elementFromPoint(clientX, clientY);
    const newIndex = [...document.querySelectorAll('.image-list-image')].indexOf(element?.closest('.image-list-image'));

    if(newIndex !== -1 && draggedIndex !== null && draggedIndex !== newIndex) {
      setDraggedOverIndex(newIndex);

      const updatedImages = [...images];
      const [movedItem] = updatedImages.splice(draggedIndex, 1);
      updatedImages.splice(newIndex, 0, movedItem);
  
      setDraggedIndex(newIndex);
      setImages(updatedImages);
      if (onListChange) onListChange(updatedImages);
    }
  };

  const dragImageClean = () => {
    setDraggedIndex(null);
    setDraggedOverIndex(null);

    if (dragImageRef.current) {
      document.body.removeChild(dragImageRef.current);
      dragImageRef.current = null;
    }

    document.querySelectorAll(".image-list-gallery-dragging-preview").forEach((el) => el.remove());
  }

  const handleDragEnd = () => {
    dragImageClean();
    document.removeEventListener("mousemove", handleDragMove);
    document.removeEventListener("mouseup", handleDragEnd);
    document.removeEventListener("touchmove", handleDragMove);
    document.removeEventListener("touchend", handleDragEnd);
  };

  const getImage = (id) => {
    if(mode==="edit") {
      if (typeof id === 'number' && !isNaN(id)) {
        return config.photos[module]+module+"/"+name+"/icon_"+id+".jpg?v=" +timestamp;
      }
    }
    return id;
  };

  return (
    <div key="image-list-gallery" className="image-list-panel">
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept="image/*"
        multiple
        onChange={handleFileChange}
      />

      {isEmpty && (
        <div className="image-list-border" onClick={addImage}>
          <IconPhotoSelect />
          {t.edit.PhotoSelect}
        </div>
      )}

      {!isEmpty && <div className="image-list-hold">{t.edit.PhotoChangeOrder}</div>}

      {!isEmpty && (
        <div className="image-list-gallery" onContextMenu={(event) => event.preventDefault()} ref={listGalleryRef}>
          {images.map((image, index) => (
            image!==undefined ? (
              <div
                key={'image-list-gallery-'+index}
                className={`image-list-image ${index === draggedOverIndex ? 'drag-over' : ''}`}
                onMouseDown={(event) => handleDragStart(event, index)} 
                onMouseMove={handleDragMove}
                onMouseUp={handleDragEnd}
                onMouseOver={(event) => event.preventDefault()}
                onTouchStart={(event) => handleDragStart(event, index)}
                onTouchMove={handleDragMove}
                onTouchEnd={handleDragEnd}
              >
                <Image src={getImage(image)} alt={`img-${index}`} width="100%" />
                {isDelete&&(<div 
                  className="image-list-image-delete" 
                  onClick={(event) => {
                    event.stopPropagation();  
                    handleDelete(index);
                  }}
                  onMouseDown={(event) => event.stopPropagation()} 
                  onTouchStart={(event) => event.stopPropagation()} 
                ></div>)}
              </div>
            ) : (
              <div
                key={'image-list-gallery-'+index}
                className={`image-list-image`}
              >
                <div className="image-list-image-placeholder">
                  <div className="button-loading-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              </div>
            )
          ))}

          {images.length < max && (
            <div className="image-list-delete" onClick={addImage}>
              <div className="image-list-image-add">+</div>
            </div>
          )}
        </div>
      )}

      {isEmpty && <Button type="priority" onClick={addImage}>{t.edit.PhotoAdd}</Button>}
    </div>
  );
};

export default InputGallery;